import type { ComputedRef, Ref } from 'vue'
import { SessionStore } from '~/stores/session'

export type PushToSaveScenarios =
  | 'add-needs'
  | 'saved-plan'
  | 'header-cta'
  | 'drugs-doctors-save-progress-modal'
  | 'drugs-doctors-lock-matches-inline'
  | 'medicare-info-save-progress'
  | 'exit-intent-urgency-save-progress'
  | 'save-your-match'
  | 'back-from-started-app'
  | 'back-from-quotes'
  | 'iep-set-up-reminders'
  | 'preshop-apply'
  | 'page-sign-up'

// Track which dialog scenario reached success message
export const pushToSaveCompletedScenario = ref<PushToSaveScenarios | null>(null)

export default class PushToSave {
  static use(scenario: PushToSaveScenarios, dialogId: string = 'push-to-save') {
    const { show } = DialogController.use()
    const { flag } = FeatureFlags.use()
    const session = SessionStore.use()
    const { track } = Analytics.use()

    const { isAuthenticated } = Auth.use()

    const _scenario = computed(() => _camelCase(scenario))

    const enabled = computed(
      () => scenario === 'header-cta' || flag<boolean>(`push-to-save-${scenario}`).value
    )

    const maybePushToSave = () => {
      if (
        !isAuthenticated.value &&
        enabled.value &&
        (scenario === 'header-cta' ||
          !_has(session.pushedToSave, _scenario.value) ||
          !session.pushedToSave[_scenario.value])
      ) {
        show(dialogId)
        session.pushedToSave = { ...session.pushedToSave, [_scenario.value]: true }
        track('PushToSaveShown', {
          scenario: _scenario.value
        })
        return true
      }
      return false
    }

    return {
      maybePushToSave
    }
  }

  // For inline presentations whose section should stay mounted through the
  // success screen instead of disappearing the instant auth completes.
  static useInlinePersistence(
    scenario: PushToSaveScenarios | null | Ref<PushToSaveScenarios | null>,
    hasDataToSave: Ref<boolean> | ComputedRef<boolean>
  ) {
    const { isAuthenticated } = Auth.use()

    const hasSaved = ref(false)

    watch(
      isAuthenticated,
      (newVal, oldVal) => {
        if (newVal && !oldVal && unref(hasDataToSave)) hasSaved.value = true
      },
      { flush: 'sync' }
    )

    watch(pushToSaveCompletedScenario, (completedScenario) => {
      if (completedScenario && completedScenario !== unref(scenario)) hasSaved.value = false
    })

    const stayVisible = computed(
      () => unref(hasDataToSave) && (!isAuthenticated.value || hasSaved.value)
    )

    return {
      hasSaved,
      stayVisible
    }
  }
}
